import { Module } from 'vuex'
import { StateInterface } from '@/store'
import Directr from '@/services/directr'
import Sys from '@/services/sys'
import Cdn from '@/services/cdn'
import { Notify } from 'quasar'
import { errorToReadableString } from '@/utils/error'

// TODO make generic constants
const HNAMES = {
  __DEV_REFRESH_TOKEN: 'x-devonly-ozone-refresh-token',
  REFRESH_TOKEN: 'x-ozone-refresh-token',
  TOKEN: 'x-ozone-token'
}
const CNAMES = {
  REFRESH_TOKEN: 'ozn__refresh_token',
  TOKEN: 'ozn__token'
}

export interface DrawerModel {
  opened: boolean;
  withoutAnimation: boolean;
}

export enum ErrorSeverityLvl {
  'Warn' = 'warn',
  'Default' = 'default',
  'Fatal' = 'fatal',
  'Unauthorized' = 'unauthorized'
}

// enum ServiceType {
//   Sys = 'Sys',
//   Directr = 'Directr',
//   Cdn = 'Cdn'
// }

const serviceConstructors = { Sys, Directr, Cdn }

export interface ServicesState extends StateInterface {
  [key: string]: any
}

export interface ServiceEventHandlers {
  Warn(error:any):Promise<any>,
  Error(error:any):Promise<any>,
  Fatal(error:any):Promise<any>,
  Unauthorized(error:any):Promise<any>
}

// default handlers for generated services
// TODO define more specific behaviours
export const DEFAULT_SERVICE_EVENT_HANDLERS = state => dispatch => {
  return {
    Warn: error => {
      return dispatch('Error', { error, severity: ErrorSeverityLvl.Warn })
    },
    Error: error => {
      return dispatch('Error', { error, severity: error.toString().includes('code 403') ? ErrorSeverityLvl.Unauthorized : ErrorSeverityLvl.Default })
    },
    Fatal: error => {
      return dispatch('Error', { error, severity: ErrorSeverityLvl.Fatal })
    },
    Unauthorized: error => {
      return dispatch('Error', { error, severity: ErrorSeverityLvl.Unauthorized })
    }
  } as ServiceEventHandlers
}

function parseErrorMessage (error) {
  const e = error?.message || error?.data
  // console.error(e)
  return errorToReadableString(e) || 'An Error occured'
  // return typeof e === 'string' && e.includes('violates foreign key') 'An Error occured'
}

const ServicesModule: Module<ServicesState, StateInterface> = {
  namespaced: true,
  state: {
    Sys: new Sys(),
    Directr: new Directr(),
    Cdn: new Cdn()
  },
  mutations: {
    // SetHost: (state: ServicesState, { service, host }) => {
    //   // state[service].setHost(host)
    // },
    Replace: (state: ServicesState, { service, instance }) => {
      state[service] = instance
    }
  },
  getters: {
    Sys: state => state.Sys,
    Directr: state => state.Directr,
    Cdn: state => state.Cdn,
    List: state => Object.keys(state),
    ServiceEventHandlers: state => DEFAULT_SERVICE_EVENT_HANDLERS(state)
  },
  actions: {
    ReplaceService: ({ commit }, { service, instance }) => {
      commit('Replace', { service, instance })
    },
    Generate: ({ dispatch, rootGetters, getters }, services = []) => {
      // console.log('[Services/Generate]', services)

      // NOTE generates intances based on available services, and optionally sets them to the state
      const instances = services.map(({ name, props: { user, host, application, token, rftoken, io }, set }) => {
        if (!serviceConstructors[name]) {
          console.warn(`[Services/Generate] Invalid service name ${name}`)
          return { service: name, error: `[Services/Generate] Invalid service name ${name}` }
        }
        // (Re-)Create any instances needing the host context
        const instance = new serviceConstructors[name]({
          debug: true,
          service: { id: name.toLowerCase(), name, io },
          user,
          requestConfig: {
            baseURL: process.env.NODE_ENV === 'development' && window.location.host.includes('192.')
              ? `http://${window.location.host.split(':')[0]}:8080`
              : (process.env.NODE_ENV === 'development' && host.url?.startsWith('https://')) ? host.url.replace('https://', 'http://') : host.url,
            withCredentials: true,
            headers: {
              'x-ozone-app-type': process.env.OZONE_SERVICE_TYPE_ID, // defined in ozone @ /constants/services
              'x-ozone-app-id': application,
              'x-ozone-user-id': user.id,
              'x-app-version': '1',
              Authorization: `Bearer ${process.env.API_AUTH_TOKEN}`,
              [HNAMES.TOKEN]: token,
              [HNAMES.REFRESH_TOKEN]: rftoken
            }
          }
        }, { getters: rootGetters, dispatch, handlers: getters.ServiceEventHandlers(dispatch) })

        if (set === true) dispatch('ReplaceService', { service: name, instance }).catch(console.error)

        return { service: name, instance }
      })

      return instances
    },
    Error: ({ dispatch }, { error, severity }) => {
      // console.log('Dispatch Error', error, severity)

      // register the error and handle UI
      switch (severity) {
        case ErrorSeverityLvl.Warn:
          // inform
          console.warn('Service Error.Warn', error)
          // Notify.create({ type: 'warn', message: parseErrorMessage(error), classes: 'custom-notify warn' })
          return Promise.reject(error)
        case ErrorSeverityLvl.Fatal:
          // inform and reload
          console.error('Service Error.Fatal')
          // Notify.create({ type: 'error', message: parseErrorMessage(error), classes: 'custom-notify error' })
          throw Error(error)
        case ErrorSeverityLvl.Unauthorized:
          // logout
          console.log('Service Error.Unauthorized | logging out...')
          // Notify.create({ type: 'error', message: parseErrorMessage(error), classes: 'custom-notify error' })
          return dispatch('User/Logout', null, { root: true }).then(res => Promise.reject(true)).catch(console.error)
        case ErrorSeverityLvl.Default:
        default:
          // inform and possibly reset state to previous state
          console.error('Service Error.Default', error)
          // Notify.create({ type: 'error', message: parseErrorMessage(error), classes: 'custom-notify error' })
          return Promise.reject(error)
      }
    }
    // Act({ commit, state }, { service, args }) {
    //   // return state[service]
    // }
  }
}

export default ServicesModule
